import styled from '@emotion/styled';
import {
  AdminPanelSettings,
  DescriptionOutlined,
  FeedbackOutlined,
  LogoutOutlined,
  Person,
  VerifiedUser,
} from '@mui/icons-material';
import {
  AppBar,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import JBT_MAREL_LOGO from '../assets/marel-mark-white.png';
import FeedbackDialog from './FeedbackDialog';
import { useAuth } from './Providers/AuthProvider';
import { useJoyride } from './Providers/JoyrideProvider';

const IconContainer = styled('span')({
  color: 'gray',
  transition: 'all 0.2s ease-in-out',
  display: 'flex',

  span: {
    color: 'black',
    marginLeft: '5px',
  },
});

const LogoContainer = styled('div')({
  width: '140px',
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  cursor: 'pointer',
  marginRight: '10px',
});

const RoutesContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  minHeight: 'inherit',
  alignItems: 'center',
});

const NavBarContainer = styled('div')({
  display: 'flex',
  gap: '5px',
  alignItems: 'center',
  width: '100%',
  minHeight: 'inherit',
  justifyContent: 'space-between',
});

const StyledAppBar = styled(AppBar)({
  backgroundColor: '#0087CD',
  color: 'white',
  boxShadow: '0px 2px 2px 2px rgba(16, 24, 40, 0.05)',
  zIndex: '1000',
});

const NavBarMenuItem = styled(MenuItem)({
  '&:hover': {
    backgroundColor: '#fff',
    color: '#000',
    padding: '5px 12px',
    margin: '0px 10px',
  },
  '.MuiTouchRipple-child': {
    backgroundColor: '#0087CD',
  },
  borderRadius: '4px',
  marginRight: '10px',
});

const ProfilePhotoButton = styled(IconButton)({
  position: 'relative',
});

const ProfilePhoto = styled('img')({
  width: '40px',
  height: '35px',
  borderRadius: '50%',
});

const AdminIcon = styled(VerifiedUser)({
  position: 'absolute',
  left: '27px',
  top: '25px',
  color: 'green',
  height: '15px',
  width: '14px',
});

type PageRoute = {
  name: string;
  route: string;
};

const defaultRoutes = [
  {
    name: 'Chat',
    route: '/chat',
  },
  {
    name: 'Prompts',
    route: '/prompts',
  },
  {
    name: 'My Prompts',
    route: '/my-prompts',
  },
  {
    name: 'FAQ',
    route: '/faqs',
  },
];

const NavBar = (): JSX.Element => {
  const { logout, currentUser, isAdmin } = useAuth();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false);
  const [routes, setRoutes] = useState<PageRoute[]>(defaultRoutes);

  const navigate = useNavigate();
  const location = useLocation();
  const { tourState, setTourState } = useJoyride();
  const profilePhoto = localStorage.getItem('profilePhoto');

  useEffect(() => {
    if (isAdmin) {
      const adminRoutes = [...defaultRoutes];
      adminRoutes.push({
        name: 'User Stats (Admin)',
        route: '/user-stats',
      });
      setRoutes(adminRoutes);
    } else {
      setRoutes(defaultRoutes);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!currentUser?.metadata.tourCompleted && tourState.tourActive === true) {
      setTourState({ ...tourState, ...{ stepIndex: 0, run: true, tourActive: false } });
    }
  });

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    setAnchorElUser(null);
    logout();
  };

  const handleFeedback = () => {
    setFeedbackOpen(true);
  };

  const handleReleaseNotes = () => {
    setAnchorElUser(null);
    navigate('/release-notes');
  };

  return (
    <StyledAppBar position="relative">
      <FeedbackDialog isOpen={feedbackOpen} setIsOpen={setFeedbackOpen} />
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          <NavBarContainer>
            {/* TODO figure out a better way to handle this */}
            {currentUser ? (
              <>
                <LogoContainer onClick={() => navigate('/')}>
                  <img src={JBT_MAREL_LOGO} alt="JBT Marel" style={{ height: '40px' }} />
                </LogoContainer>
                <RoutesContainer>
                  {routes.map((page) => (
                    <NavBarMenuItem
                      key={page.name}
                      id={page.route.substring(1)}
                      onClick={async () => {
                        navigate(page.route);
                      }}
                      selected={location.pathname.startsWith(page.route)}
                    >
                      <Typography
                        textAlign="center"
                        sx={{
                          backgroundColor: location.pathname.startsWith(page.route) ? '#fff' : '',
                          borderRadius: location.pathname.startsWith(page.route) ? '5px' : '',
                          color: location.pathname.startsWith(page.route) ? '#000' : '',
                          padding: location.pathname.startsWith(page.route) ? '5px 12px' : '',
                        }}
                      >
                        {page.name}
                      </Typography>
                    </NavBarMenuItem>
                  ))}
                </RoutesContainer>
                <Tooltip
                  title={<Typography variant="caption"> Submit Feedback </Typography>}
                  sx={{ height: '40px', width: '40px' }}
                >
                  <IconButton onClick={() => handleFeedback()}>
                    <FeedbackOutlined sx={{ color: 'white' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={<Typography variant="caption"> Open settings </Typography>}
                  sx={{ height: '40px', width: '40px' }}
                >
                  <ProfilePhotoButton onClick={handleOpenUserMenu}>
                    {isAdmin && profilePhoto ? (
                      <>
                        <ProfilePhoto src={profilePhoto} alt="Profile" />
                        <AdminIcon />
                      </>
                    ) : isAdmin && !profilePhoto ? (
                      <AdminPanelSettings sx={{ color: 'white' }} />
                    ) : profilePhoto ? (
                      <ProfilePhoto src={profilePhoto} alt="Profile" />
                    ) : (
                      <Person sx={{ color: 'white' }} />
                    )}
                  </ProfilePhotoButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem onClick={() => handleReleaseNotes()}>
                    <Typography textAlign="center">
                      <IconContainer>
                        <DescriptionOutlined />
                        <span>Release Notes</span>
                      </IconContainer>
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleLogout()}>
                    <Typography textAlign="center">
                      <IconContainer>
                        <LogoutOutlined />
                        <span>Logout</span>
                      </IconContainer>
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <LogoContainer onClick={() => navigate('/')}>
                <img src={JBT_MAREL_LOGO} alt="JBT Marel" style={{ height: '40px' }} />
              </LogoContainer>
            )}
          </NavBarContainer>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
};

export default NavBar;
